import React from 'react';
import { Brain, Search, Palette, Layout, MessageSquare, ShieldCheck, Target, Zap, Image as ImageIcon, Star, FileText } from 'lucide-react';

export const AgentIntelligenceView: React.FC = () => {
    return (
        <div className="flex-1 overflow-y-auto bg-zinc-50 p-8 custom-scrollbar">
            <div className="max-w-5xl mx-auto space-y-12">
                {/* Header */}
                <div className="border-b border-zinc-200 pb-8">
                    <div className="flex items-center gap-3 text-orange-700 font-bold uppercase tracking-widest text-xs mb-4">
                        <Brain size={16} /> Inteligência Artificial Ditho
                    </div>
                    <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-4">
                        O Cérebro do Agente Gerador
                    </h1>
                    <p className="text-slate-500 text-lg leading-relaxed max-w-3xl">
                        Documentação técnica e estratégica da lógica por trás de cada site criado. 
                        Este sistema foi projetado para criar sites de R$ 1.000,00+, focando em conversão, 
                        autoridade visual e personalização extrema por nicho.
                    </p>
                </div>

                {/* Grid de Pilares */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Pilar 1: Análise Multimodal */}
                    <div className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm space-y-6">
                        <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center rounded-sm">
                            <ImageIcon size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">1. Identidade Visual (Visão)</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            O agente extrai o DNA da marca. Se houver uma foto de perfil 
                            do WhatsApp (Logo), ele identifica as cores exatas (HEX) e o estilo para usar como base de toda a interface.
                        </p>
                        <ul className="space-y-3 text-xs font-mono text-slate-500">
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-green-600 shrink-0" />
                                Extração de cores primárias da LOGO para variáveis --primary.
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-green-600 shrink-0" />
                                Fim do Modo Escuro forçado: O tema segue a claridade da logo.
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-green-600 shrink-0" />
                                Curadoria de Fotos: IA decide onde fotos de serviço vs ambiente se encaixam melhor.
                            </li>
                        </ul>
                    </div>

                    {/* Pilar 2: Estrutura de Copywriting */}
                    <div className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm space-y-6">
                        <div className="w-12 h-12 bg-orange-700 text-white flex items-center justify-center rounded-sm">
                            <FileText size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">2. Copywriting Persuasivo</h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                            Estrutura massiva de 10 seções focada na jornada do cliente. A copy é gerada para 
                            atacar as objeções e segmentar o público alvo.
                        </p>
                        <ul className="space-y-3 text-xs font-mono text-slate-500">
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-orange-600 shrink-0" />
                                Seção Exclusiva por Nicho (Criatividade Contextual).
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-orange-600 shrink-0" />
                                Uso de Depoimentos REAIS (Filtro Anti-Bot).
                            </li>
                            <li className="flex items-start gap-2">
                                <Check size={14} className="text-orange-600 shrink-0" />
                                FAQ Estratégico para quebra de objeções.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Blueprint do Site Perfeito */}
                <div className="bg-slate-900 rounded-sm p-10 text-white space-y-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-8">
                        <div>
                            <h2 className="text-2xl font-bold">Blueprint do Site de Alta Conversão</h2>
                            <p className="text-slate-400 text-sm mt-1">A sequência lógica que o agente deve seguir SEMPRE.</p>
                        </div>
                        <div className="bg-slate-800 px-4 py-2 rounded-sm border border-slate-700 text-xs font-bold font-mono">
                            VERIFICADO: 10 SEÇÕES
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { id: '01', title: 'Header Estratégico', desc: 'Logo à esquerda, CTA no canto superior direito para máxima visibilidade.' },
                            { id: '02', title: 'Hero de Impacto', desc: 'Foto real + Headline matadora focada no Problema + Solução.' },
                            { id: '03', title: 'Dor/Problema', desc: 'Conexão emocional imediata através da empatia com a dor do lead.' },
                            { id: '04', title: 'Para Quem', desc: 'Segmentação explícita de público para aumentar a qualificação.' },
                            { id: '05', title: 'Solução Detalhada', desc: 'O que entregamos de forma clara, técnica e profissional.' },
                            { id: '06', title: 'Seção Exclusiva', desc: 'Ponto de diferencial criado especificamente para o lead.' },
                            { id: '07', title: 'Galeria Social', desc: 'Uso maciço das fotos do ZIP em bento-grid ou alvenaria.' },
                            { id: '08', title: 'Depoimentos Google', desc: 'Nomes, estrelas e textos das reviews reais fornecidas.' },
                            { id: '09', title: 'FAQ Inteligente', desc: 'Respostas para dúvidas que impedem o clique no botão.' },
                            { id: '10', title: 'Rodapé Profissional', desc: 'Mapa, endereço, horários e links sociais consolidados.' }
                        ].map((s) => (
                            <div key={s.id} className="bg-slate-800/50 border border-slate-700 p-6 rounded-sm hover:border-orange-700 transition-colors group">
                                <div className="text-orange-500 font-mono text-xs mb-2 font-bold group-hover:scale-110 transition-transform inline-block">SECTION_{s.id}</div>
                                <h4 className="font-bold text-lg mb-2">{s.title}</h4>
                                <p className="text-slate-400 text-xs leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Prompt Técnico Real */}
                <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Zap size={24} className="text-orange-700" /> Diretrizes de Design & Visão (Prompt Técnico)
                    </h3>
                    <div className="bg-white border border-zinc-200 rounded-sm p-8 shadow-sm font-mono text-[11px] leading-relaxed text-slate-600 h-96 overflow-y-auto custom-scrollbar">
                        <p className="font-bold text-slate-900 mb-4 underline uppercase">{" >>> SISTEMA DE DESIGN & CONTRASTE <<< "}</p>
                        1. DNA DA MARCA: Identifique a cor predominante da LOGO e use-a como --primary. O site deve ser uma extensão da marca.<br/>
                        2. CONTRASTE REAL: Se o fundo for escuro, texto DEVE ser claro. Se o fundo for claro, texto DEVE ser escuro.<br/>
                        3. RITMO VISUAL: Alterne as cores de fundo entre as seções para criar profundidade.<br/>
                        4. MOBILE FIRST: Escreva CSS pensando primeiro no celular e expanda com 'md:'.<br/>
                        5. INTERATIVIDADE: Use transições suaves em hover nos botões e links.<br/><br/>

                        <p className="font-bold text-slate-900 mb-4 underline uppercase">{" >>> CURADORIA DE IMAGENS POR VISÃO <<< "}</p>
                        - FOTOS DE SERVIÇOS: Use na seção "Serviços".<br/>
                        - FOTOS DE LOCAL/AMBIENTE: Use na seção "Sobre".<br/>
                        - FOTOS DE PRODUTOS: Use na "Galeria/Acervo".<br/>
                        - EXTRAÇÃO DE VIBE: (Rústico, Moderno, Industrial, Minimalista) aplicado ao layout.<br/><br/>

                        <p className="font-bold text-slate-900 mb-4 underline uppercase">{" >>> REGRAS DE IMAGENS REAIS <<< "}</p>
                        1. Para a LOGO (WhatsApp Perfil): Use src="[LOGO]"<br/>
                        2. Para as FOTOS LOCAIS (ZIP/PASTA): Use src="[IMG_0]", src="[IMG_1]", etc.<br/>
                        3. Para as FOTOS REMOTAS (Google/Links): Use src="[REMOTE_IMG_0]", etc.<br/>
                        MANTENHA A ORDEM: Tente usar as imagens locais primeiro.
                    </div>
                </div>

                {/* Rodapé da Doc */}
                <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center space-y-4 shadow-sm border-t-4 border-t-orange-700">
                    <p className="text-slate-500 text-sm">
                        "Cada site é uma obra única, moldada pelos dados do lead e pela visão computacional."
                    </p>
                    <div className="text-xs font-bold text-slate-900 uppercase tracking-tighter">
                        Gerador v3.0 • Engine: Gemini 1.5 Pro Multimodal • Vision Control Enabled
                    </div>
                </div>
            </div>
        </div>
    );
};

const Check = ({ size, className }: { size: number, className: string }) => (
    <svg 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
